'use client';

import type { ReactNode } from 'react';

import { ORDERS_COPY } from '@eliadmin/core/src/components/dlz/account/orders/orders-copy';
import {
  stageFilter,
  type OrderStage,
} from '@eliadmin/core/src/components/dlz/account/orders/order-stage';
import type { ReorderResult } from '@eliadmin/core/src/components/dlz/account/orders/use-reorder';
import { Icon, type IconName } from '@eliadmin/core/src/components/dlz/ui/icon';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { cn } from '@eliadmin/core/src/utils/style-utils';

const PLACEHOLDER = '/images/placeholder.png';

/**
 * Red DLZ/Body/S Medium text actions of the cards („Faktúra (PDF)“, „Kúpiť znova“, „Reklamovať“).
 */
export const TEXT_ACTION =
  'dlz-body-s-medium rounded-sm text-[#D7191F] transition-colors hover:text-[#B5141A] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012] disabled:cursor-default disabled:opacity-60';

/** The design draws the 18 px icons with a 1.5 px line (2 px on the 24 px grid). */
export const ICON_STROKE = '[&_[stroke]]:[stroke-width:2]';

const PILL_TONES = {
  active: 'bg-[#FEF6E7] text-[#9A5F00]',
  delivered: 'bg-[#ECF7EF] text-[#16753A]',
  cancelled: 'bg-[#ECEDEF] text-[#6B7079]',
} as const;

/**
 * Figma „Status · …“ (60317:25772): the shop's status name on a pill coloured by its stage –
 * warning while in progress, success when delivered, subtle when cancelled. 36 px, 32 in a narrow
 * column.
 */
export function OrderStatusPill({
  name,
  stage,
  className,
}: {
  name: string;
  stage: OrderStage;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'dlz-body-s-medium inline-flex h-8 shrink-0 items-center whitespace-nowrap rounded-full px-3 @[30rem]:h-9',
        PILL_TONES[stageFilter(stage)],
        className,
      )}
    >
      {name}
    </span>
  );
}

/** Figma „Thumb“: product image on white, 1 px border/subtle, radius 10. */
export function OrderItemImage({
  url,
  alt = '',
  size,
}: {
  url?: string | null;
  alt?: string;
  size: 56 | 64;
}) {
  return (
    <span
      className={cn(
        'relative block shrink-0 overflow-hidden rounded-[10px] border border-[#E2E4E7] bg-white',
        size === 56 ? 'size-14' : 'size-16',
      )}
    >
      <Image
        src={url || PLACEHOLDER}
        alt={alt}
        fill
        sizes={`${size}px`}
        unoptimized={!url}
        className="object-contain"
      />
    </span>
  );
}

/**
 * Figma „Delivery“ of a cancelled order (60459:91069): × and the status. The design's „Zrušená
 * zákazníkom 14. 8. 2026“ names who cancelled and when – the API has neither, so the line carries
 * the shop's status name.
 */
export function CancelledNote({ status, className }: { status: string; className?: string }) {
  return (
    <p className={cn('dlz-body-s flex items-center gap-2 text-[#4F545C]', className)}>
      <Icon name="x" size={18} className={cn('text-[#6B7079]', ICON_STROKE)} />
      {status}
    </p>
  );
}

/** The outcome of „Kúpiť znova“ when not every line made it into the cart. */
export function ReorderMessage({ result }: { result: ReorderResult | null }) {
  const { t } = useTranslations();
  if (!result) return null;
  return (
    <p role={result.success ? 'status' : 'alert'} className={cn('dlz-body-s', !result.success && 'text-[#D7191F]')}>
      {result.messages.map((message, index) => <span key={message}>{index > 0 && <br />}{message}</span>)}
      {result.showCart && <> {' '}<Link href={ROUTES.CART()} prefetch={false} className={cn(TEXT_ACTION, 'underline')}>{t('text456')}</Link></>}
      {result.status === 401 && <> {' '}<Link href={ROUTES.SIGN_IN(ROUTES.ORDERS())} prefetch={false} className={cn(TEXT_ACTION, 'underline')}>{t('text189')}</Link></>}
    </p>
  );
}

/**
 * Empty, „nothing found“, error and not-found states (no Figma design): the empty-state card of the
 * cart (Figma „Prázdny košík“ 60256:16234 – bg/surface, 48 px brand icon, centred texts) at the
 * scale of the account column.
 */
export function OrdersMessage({
  icon,
  title,
  text,
  action,
  alert,
  heading: Heading = 'h2',
}: {
  icon: IconName;
  title: string;
  text?: string;
  action?: ReactNode;
  /** Errors are announced right away. */
  alert?: boolean;
  /** `h1` where the state replaces the page's own heading (the order detail). */
  heading?: 'h1' | 'h2';
}) {
  return (
    <div
      role={alert ? 'alert' : undefined}
      className="flex flex-col items-center gap-4 rounded-[18px] bg-[#F5F6F7] px-4 py-8 text-center md:gap-6 md:p-12"
    >
      <Icon name={icon} size={48} className="text-[#D7191F]" />
      <div className="flex max-w-[480px] flex-col items-center gap-2">
        <Heading className="dlz-h4 text-[#0F1012]">{title}</Heading>
        {text ? <p className="dlz-body-m text-[#4F545C]">{text}</p> : null}
      </div>
      {action}
    </div>
  );
}
